import { getHeaderSectionHeight } from "../helpers/HeaderSection";

/**
 * @class CollectionPagination
 */
class CollectionPagination {
  constructor() {
    this.data = [];

    window.requestAnimationFrame(() => {
      this.refresh();
    });
  }

  refresh() {
    this.areas = document.querySelectorAll('[data-filter-content]');

    const variantFromCookie = this.getPaginationVariant();
    this.autoLoad = variantFromCookie === "load_more_auto";
    this.variant =
      variantFromCookie === "load_more_auto" ? "load_more" : variantFromCookie;

    document
      .querySelectorAll(
        "[data-collection-products] [data-pagination-loading-overlay]"
      )
      .forEach((overlay) => overlay.remove());

    this.showVariantPagination();
    this.listenerClickButtons();
    this.initObserver();

    if (this.autoLoad) {
      this.initLoadMoreAuto();
    }
  }

  initObserver() {
    const productsWrapper = document.querySelector(
      "[data-collection-products]"
    );
    if (!productsWrapper || !window.MutationObserver) return;

    if (this.observer) {
      this.observer.disconnect();
    }

    const observer = new MutationObserver(() => {
      this.showVariantPagination();
    });

    observer.observe(productsWrapper, { childList: true, subtree: true });
    this.observer = observer;
  }

  showVariantPagination() {
    document
      .querySelectorAll("[data-collection-products] [data-pagination-wrapper]")
      .forEach((wrapper) => {
        const system = wrapper.dataset.system;
        if (system === this.variant) {
          wrapper.classList.remove("hidden");
        } else {
          wrapper.classList.add("hidden");
        }
      });
  }

  getPaginationVariant() {
    // const match = document.cookie.match(/(?:^|;\s*)pagination_variant=(\w+)/);
    // if (match && match[1]) {
    //   return match[1];
    // }
    return (
      document.querySelector("[data-collection-products]")?.dataset
        ?.paginationSystem || "load_more"
    );
  }

  listenerClickButtons() {
    [...this.areas].forEach((area) => {
      if (area.dataset.paginationEventsBound === "true") {
        return;
      }
      area.dataset.paginationEventsBound = "true";
      area.addEventListener("click", (event) => {
        this.clickButton(event);
      });
    });
  }

  async clickButton(event) {
    const item = event.target.closest(
      "[data-pagination-link], [data-pagination-load-more]"
    );

    if (item) {
      event.preventDefault();

      const wrapper = item.closest("[data-collection-products]");
      const pagination_wrapper = item.closest("[data-pagination-wrapper]");
      const pagination_system =
        pagination_wrapper?.dataset?.system || "default";

      if (wrapper && !item.classList.contains("twc-btn--loading")) {
        item.classList.add("twc-btn--loading");
        wrapper.dataset.paginationLoading = '';

        const url = this.setURL(item.getAttribute("href"), event);
        const data = await this.getData(url);
        if (data) {
          const products = new DOMParser()
            .parseFromString(data, "text/html")
            .querySelector("[data-collection-products]");
          if (products) {
            products
              .querySelectorAll("[data-pagination-loading-overlay]")
              .forEach((overlay) => overlay.remove());
            if (pagination_system === "load_more") {
              this.renderItemsLoadMore(products, item, wrapper);
            } else {
              wrapper.innerHTML = products.innerHTML;
            }
            this.updateURLHash(url);
            this.showVariantPagination();
          }
        }

        if (window.LazyLoaderRun) {
          window.LazyLoaderRun();
        }
        if (window.dy_productCardPerformance) {
          window.dy_productCardPerformance();
        }

        window.callbacks_pagination_after_change =
          window.callbacks_pagination_after_change || [];
        if (window?.callbacks_pagination_after_change.length) {
          window.callbacks_pagination_after_change.forEach((callback) =>
            callback()
          );
        }

        delete wrapper.dataset.paginationLoading;
        item.classList.remove("twc-btn--loading");
        this.srollToTopSection(item, wrapper, pagination_system);
      }
    }
  }

  renderItemsLoadMore(products, btn, wrapper) {
    const items = [
      ...products.querySelectorAll("[data-collection-product-item]"),
    ];
    const paginationWrapperPrev = products.querySelector(
      '[data-pagination-direction="prev"]'
    );
    const paginationWrapperNext = products.querySelector(
      '[data-pagination-direction="next"]'
    );
    const list = wrapper.querySelector("[data-collection-products-list]");

    if (items.length) {
      if (btn.dataset.button === "prev") {
        items.reverse().forEach((item) => list.prepend(item));

        if (paginationWrapperPrev) {
          wrapper.querySelector('[data-pagination-direction="prev"]').innerHTML =
            paginationWrapperPrev.innerHTML;
        }
      } else if (btn.dataset.button === "next") {
        items.forEach((item) => list.appendChild(item));

        if (paginationWrapperNext) {
          wrapper.querySelector('[data-pagination-direction="next"]').innerHTML =
            paginationWrapperNext.innerHTML;
        }
      }
    }

    if (this.autoLoad) {
      this.initLoadMoreAuto();
    }
  }

  srollToTopSection(btn, wrapper, pagination_system) {
    let scrollBreakpoint =
      wrapper.getBoundingClientRect().top -
      getHeaderSectionHeight() -
      20 +
      window.scrollY;

    if (pagination_system === "load_more" && btn.dataset.button === "next") {
      scrollBreakpoint = window.scrollY;
    }

    if (scrollBreakpoint) {
      window.scrollTo({ top: scrollBreakpoint, behavior: "smooth" });
    }
  }

  setURL(url, event) {
    const urlData = new URL(url, window.location.origin);
    const searchParams = urlData.searchParams;
    const wrapper = event.target.closest(
      '[data-collection-section]'
    );
    if (wrapper?.dataset?.sectionId) {
      searchParams.set("section_id", wrapper.dataset.sectionId);
    }
    if (!searchParams.has("limit")) {
      const products = event.target.closest("[data-collection-products]");
      if (products?.dataset?.limit) {
        searchParams.set("limit", products.dataset.limit);
      }
    }

    return `${urlData.pathname}${urlData.search}`;
  }

  async getData(url) {
    const filterDataUrl = (element) => element.url === url;
    const data = this.data.some(filterDataUrl)
      ? this.getDataFromCache(filterDataUrl)
      : await this.getDataFromFetch(url);
    return data;
  }

  getDataFromCache(filterDataUrl) {
    const html = this.data.find(filterDataUrl).html;
    return html;
  }

  async getDataFromFetch(url) {
    const query = await fetch(url);
    const responseText = await query.text();
    if (responseText) {
      const html = responseText;
      this.data = [...this.data, { html, url }];
      return html;
    }
    return null;
  }

  updateURLHash(url) {
    url = window.location.origin + url;
    const url_data = new URL(url);
    const searchParams = new URLSearchParams(url_data.search);
    searchParams.delete("view");
    searchParams.delete("section_id");

    if (searchParams.get("page") === "1") {
      searchParams.delete("page");
    }

    const params = searchParams.toString();
    history.pushState(
      { params },
      "",
      `${window.location.pathname}${params && "?".concat(params)}`
    );
  }

  initLoadMoreAuto() {
    const btnNext = document.querySelector(
      "[data-collection-products] [data-pagination-direction='next'] [data-pagination-load-more][data-button='next']"
    );

    if (!btnNext) return;

    Object.assign(btnNext.style, {
      background: "none",
      textIndent: "-1000em",
      pointerEvents: "none",
    });

    if (this.loadMoreObserver) {
      this.loadMoreObserver.disconnect();
    }

    this.loadMoreObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (
            entry.isIntersecting &&
            !btnNext.classList.contains("twc-btn--loading")
          ) {
            btnNext.click();
          }
        });
      },
      {
        rootMargin: "0px 0px 0px 0px",
        threshold: 0.1,
      }
    );

    this.loadMoreObserver.observe(btnNext);
  }
}

export default CollectionPagination;
